import React from 'react';

export interface MatrixRow {
    dimension: string;
    metric: string;
    howMeasured: string;
    thresholdOrTarget: string;
    currentValue?: string | number; // Optional for now, can be used to show actual vs target
    status?: 'pass' | 'fail' | 'warning' | 'audit';
}

export interface EvaluationMatrixProps {
    title: string;
    description?: string;
    columns: string[];
    data: MatrixRow[];
    colorTheme?: 'indigo' | 'green' | 'blue' | 'purple' | 'amber' | 'rose';
}

const EvaluationMatrix: React.FC<EvaluationMatrixProps> = ({
    title,
    description,
    columns,
    data,
    colorTheme = 'indigo'
}) => {

    const getThemeColors = (theme: string) => {
        switch (theme) {
            case 'green': return { header: 'bg-green-50 text-green-700', border: 'border-green-100', title: 'text-green-900', icon: 'bg-green-100 text-green-600' };
            case 'blue': return { header: 'bg-blue-50 text-blue-700', border: 'border-blue-100', title: 'text-blue-900', icon: 'bg-blue-100 text-blue-600' };
            case 'purple': return { header: 'bg-purple-50 text-purple-700', border: 'border-purple-100', title: 'text-purple-900', icon: 'bg-purple-100 text-purple-600' };
            case 'amber': return { header: 'bg-amber-50 text-amber-700', border: 'border-amber-100', title: 'text-amber-900', icon: 'bg-amber-100 text-amber-600' };
            case 'rose': return { header: 'bg-rose-50 text-rose-700', border: 'border-rose-100', title: 'text-rose-900', icon: 'bg-rose-100 text-rose-600' };
            default: return { header: 'bg-indigo-50 text-indigo-700', border: 'border-indigo-100', title: 'text-indigo-900', icon: 'bg-indigo-100 text-indigo-600' };
        }
    };

    const theme = getThemeColors(colorTheme);

    const getStatusBadge = (status?: string) => {
        if (!status) return null;
        switch (status) {
            case 'pass': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Pass</span>;
            case 'fail': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Fail</span>;
            case 'warning': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Review</span>;
            case 'audit': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Audit</span>;
            default: return null;
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm border ${theme.border} overflow-hidden mb-8`}>
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
                <div>
                    <h3 className={`text-lg font-bold ${theme.title}`}>{title}</h3>
                    {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
                </div>
                <div className={`h-8 w-8 rounded-lg ${theme.icon} flex items-center justify-center`}>
                    <i className="fas fa-chart-pie text-sm"></i>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className={`${theme.header} font-semibold uppercase text-xs border-b ${theme.border}`}>
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} className="px-6 py-3 whitespace-nowrap">{col}</th>
                            ))}
                            <th className="px-6 py-3 text-right">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4 font-medium text-slate-800">{row.dimension}</td>
                                <td className="px-6 py-4 text-slate-600">{row.metric}</td>
                                <td className="px-6 py-4 text-slate-500">
                                    <span className="inline-block bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-600">
                                        {row.howMeasured}
                                    </span>
                                </td>
                                <td className="px-6 py-4 font-semibold text-slate-700">{row.thresholdOrTarget}</td>
                                <td className="px-6 py-4 text-right">
                                    {getStatusBadge(row.status)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EvaluationMatrix;
